/**
 * Dynamic Scenario Calibration
 *
 * Extends loss scenario probability calculations by incorporating
 * technology scoping responses and sensitive data volumes.
 * Does NOT modify impactCalibration.js — wraps its output.
 */

const TECHNOLOGY_CALIBRATION_MAP = {
  'ts-2': {
    'None / Local accounts only': {
      data_breach: { probabilityMod: 1.4, reason: 'No centralized identity management' },
      financial_theft: { probabilityMod: 1.5, reason: 'No centralized credential management' },
      insider_threat: { probabilityMod: 1.3, reason: 'No centralized access control' }
    },
    'On-premises Active Directory only (no cloud sync)': {
      data_breach: { probabilityMod: 1.15, reason: 'Legacy identity platform without modern security features' },
      financial_theft: { probabilityMod: 1.1, reason: 'Limited conditional access capabilities' }
    },
    'Hybrid: On-premises AD synced to cloud IdP': {
      data_breach: { probabilityMod: 1.0, reason: 'Hybrid identity with sync complexity' },
      financial_theft: { probabilityMod: 0.95, reason: 'Mixed authentication capabilities' }
    },
    'Microsoft Entra ID (Azure AD)': {
      data_breach: { probabilityMod: 0.85, reason: 'Modern identity platform with conditional access' },
      financial_theft: { probabilityMod: 0.8, reason: 'Advanced authentication capabilities' }
    },
    'Okta': {
      data_breach: { probabilityMod: 0.85, reason: 'Enterprise identity platform' },
      financial_theft: { probabilityMod: 0.8, reason: 'Advanced MFA capabilities' }
    },
    'Google Identity': {
      data_breach: { probabilityMod: 0.9, reason: 'Cloud-native identity platform' },
      financial_theft: { probabilityMod: 0.85, reason: 'Modern authentication' }
    },
    'JumpCloud': {
      data_breach: { probabilityMod: 0.9, reason: 'Modern directory platform' },
      financial_theft: { probabilityMod: 0.85, reason: 'Cloud-native access management' }
    }
  },
  'ts-4': {
    'None': {
      cyber_extortion: { probabilityMod: 1.6, reason: 'No endpoint protection deployed' },
      data_breach: { probabilityMod: 1.4, reason: 'Endpoints unprotected' }
    },
    'Traditional antivirus only (Norton, McAfee, etc.)': {
      cyber_extortion: { probabilityMod: 1.25, reason: 'Legacy AV lacks modern detection' },
      data_breach: { probabilityMod: 1.1, reason: 'Signature-only detection' }
    },
    'Microsoft Defender for Endpoint / Intune': {
      cyber_extortion: { probabilityMod: 0.75, reason: 'Modern EDR with behavioral detection' },
      data_breach: { probabilityMod: 0.85, reason: 'Integrated endpoint protection' }
    },
    'CrowdStrike Falcon': {
      cyber_extortion: { probabilityMod: 0.7, reason: 'Industry-leading EDR platform' },
      data_breach: { probabilityMod: 0.8, reason: 'Advanced threat detection' }
    },
    'SentinelOne': {
      cyber_extortion: { probabilityMod: 0.72, reason: 'AI-driven endpoint protection' },
      data_breach: { probabilityMod: 0.82, reason: 'Autonomous detection and response' }
    },
    'Sophos': {
      cyber_extortion: { probabilityMod: 0.78, reason: 'Managed endpoint protection' },
      data_breach: { probabilityMod: 0.85, reason: 'Synchronized security' }
    },
    'Huntress': {
      cyber_extortion: { probabilityMod: 0.75, reason: 'Managed EDR with human analysis' },
      data_breach: { probabilityMod: 0.83, reason: 'Persistent threat hunting' }
    }
  },
  'ts-5': {
    'None': {
      cyber_extortion: { probabilityMod: 1.1, costMod: 2.0, reason: 'No backup — full ransom or rebuild' },
      business_interruption: { costMod: 1.8, reason: 'No recovery path' }
    },
    'Manual/ad hoc backups': {
      cyber_extortion: { costMod: 1.5, reason: 'Untested backups — recovery uncertain' },
      business_interruption: { costMod: 1.3, reason: 'Manual recovery extends downtime' }
    },
    'Veeam': {
      cyber_extortion: { costMod: 0.7, reason: 'Enterprise backup with immutable storage options' },
      business_interruption: { costMod: 0.8, reason: 'Rapid recovery capabilities' }
    },
    'Datto/Kaseya': {
      cyber_extortion: { costMod: 0.7, reason: 'MSP-grade backup with instant virtualization' },
      business_interruption: { costMod: 0.75, reason: 'Rapid failover capabilities' }
    },
    'Acronis': {
      cyber_extortion: { costMod: 0.75, reason: 'Backup with anti-ransomware protection' },
      business_interruption: { costMod: 0.8, reason: 'Automated recovery' }
    },
    'Cloud-native backup (Azure Backup, AWS Backup, Google)': {
      cyber_extortion: { costMod: 0.75, reason: 'Cloud-native backup with geo-redundancy' },
      business_interruption: { costMod: 0.8, reason: 'Cloud recovery capabilities' }
    }
  },
  'ts-6': {
    'ISP-provided router/firewall only': {
      cyber_extortion: { probabilityMod: 1.3, reason: 'No enterprise firewall capabilities' },
      data_breach: { probabilityMod: 1.2, reason: 'Minimal network security controls' }
    },
    'Fortinet (FortiGate)': {
      cyber_extortion: { probabilityMod: 0.85, reason: 'Enterprise NGFW with IPS' },
      data_breach: { probabilityMod: 0.9, reason: 'Network segmentation capabilities' }
    },
    'Palo Alto Networks': {
      cyber_extortion: { probabilityMod: 0.8, reason: 'Advanced threat prevention' },
      data_breach: { probabilityMod: 0.85, reason: 'Application-aware firewall' }
    },
    'Cisco Meraki': {
      cyber_extortion: { probabilityMod: 0.85, reason: 'Cloud-managed network security' },
      data_breach: { probabilityMod: 0.9, reason: 'Centralized management' }
    },
    'SonicWall': {
      cyber_extortion: { probabilityMod: 0.88, reason: 'SMB-focused NGFW' },
      data_breach: { probabilityMod: 0.9, reason: 'Threat prevention capabilities' }
    },
    'Cloud-based (Zscaler, Cloudflare Zero Trust)': {
      cyber_extortion: { probabilityMod: 0.75, reason: 'Zero trust network architecture' },
      data_breach: { probabilityMod: 0.8, reason: 'Cloud-native security inspection' }
    }
  }
}

const DATA_CATEGORY_COST_MULTIPLIERS = {
  'phi': { costPerRecord: 245, regulatoryMultiplier: 1.5, label: 'Protected Health Information' },
  'pii': { costPerRecord: 165, regulatoryMultiplier: 1.0, label: 'Personally Identifiable Information' },
  'pci': { costPerRecord: 195, regulatoryMultiplier: 1.3, label: 'Payment Card Data' },
  'financial-data': { costPerRecord: 185, regulatoryMultiplier: 1.2, label: 'Financial Records' },
  'financial': { costPerRecord: 185, regulatoryMultiplier: 1.2, label: 'Financial Records' },
  'student': { costPerRecord: 155, regulatoryMultiplier: 1.1, label: 'Student Records (FERPA)' },
  'donor': { costPerRecord: 120, regulatoryMultiplier: 0.8, label: 'Donor/Supporter Data' },
  'employee': { costPerRecord: 140, regulatoryMultiplier: 0.9, label: 'Employee Records' },
  'ip': { costPerRecord: 280, regulatoryMultiplier: 0.7, label: 'Intellectual Property' }
}

const ALL_SCENARIOS = ['data_breach', 'cyber_extortion', 'financial_theft', 'business_interruption', 'insider_threat', 'regulatory', 'third_party']

/**
 * Calculate comprehensive scenario calibration from all assessment data.
 */
export function calculateDynamicCalibration(
  maturityResponses,
  technologyScopingResponses,
  sensitiveDataCategories,
  businessScopingResponses
) {
  maturityResponses = maturityResponses || {}
  technologyScopingResponses = technologyScopingResponses || {}
  sensitiveDataCategories = sensitiveDataCategories || []
  businessScopingResponses = businessScopingResponses || {}
  const scenarioAdjustments = {}

  // Initialize all scenarios
  ALL_SCENARIOS.forEach(s => {
    scenarioAdjustments[s] = { probabilityModifier: 1.0, costModifier: 1.0, factors: [] }
  })

  // 1. Technology scoping adjustments
  const techResponses = technologyScopingResponses || {}
  for (const [questionId, calibrations] of Object.entries(TECHNOLOGY_CALIBRATION_MAP)) {
    const response = techResponses[questionId]
    const responseValue = response?.value || response
    if (!responseValue || !calibrations[responseValue]) continue

    const adjustments = calibrations[responseValue]
    for (const [scenario, adj] of Object.entries(adjustments)) {
      if (!scenarioAdjustments[scenario]) continue

      if (adj.probabilityMod) {
        scenarioAdjustments[scenario].probabilityModifier *= adj.probabilityMod
        scenarioAdjustments[scenario].factors.push({
          source: 'technology',
          control: `${questionId}`,
          effect: `probabilityMod: ${adj.probabilityMod}`,
          reason: adj.reason
        })
      }
      if (adj.costMod) {
        scenarioAdjustments[scenario].costModifier *= adj.costMod
        scenarioAdjustments[scenario].factors.push({
          source: 'technology',
          control: `${questionId}`,
          effect: `costMod: ${adj.costMod}`,
          reason: adj.reason
        })
      }
    }
  }

  // 2. Sensitive data volume adjustments
  const dataBreachCostByCategory = []
  let totalRecordCount = 0
  let weightedCostSum = 0

  // sensitiveDataCategories from dt-1 business scoping (array of category keys)
  const dataCategories = (Array.isArray(sensitiveDataCategories) && sensitiveDataCategories.length > 0)
    ? sensitiveDataCategories
    : (businessScopingResponses?.['dt-1']?.value || [])

  if (Array.isArray(dataCategories)) {
    dataCategories.forEach(cat => {
      const config = DATA_CATEGORY_COST_MULTIPLIERS[cat]
      if (!config) return

      // Estimate record count from business scoping context
      const recordCount = businessScopingResponses?.['data_record_count'] || 5000
      const catRecords = Math.round(recordCount / dataCategories.length)

      totalRecordCount += catRecords
      weightedCostSum += catRecords * config.costPerRecord

      dataBreachCostByCategory.push({
        category: cat,
        label: config.label,
        records: catRecords,
        costPerRecord: config.costPerRecord,
        totalCost: catRecords * config.costPerRecord,
        percentage: 0 // Calculated after
      })

      // Apply regulatory multiplier to data breach scenario
      if (config.regulatoryMultiplier > 1.0) {
        scenarioAdjustments.data_breach.costModifier *= (1 + (config.regulatoryMultiplier - 1) * 0.3)
        scenarioAdjustments.data_breach.factors.push({
          source: 'data-volumes',
          category: config.label,
          records: catRecords,
          effect: `regulatoryMultiplier: ${config.regulatoryMultiplier}`,
          reason: `${config.label} at $${config.costPerRecord}/record`
        })
      }
    })
  }

  // Calculate percentages
  if (totalRecordCount > 0) {
    dataBreachCostByCategory.forEach(c => {
      c.percentage = Math.round((c.totalCost / weightedCostSum) * 100)
    })
  }

  const weightedAverageCostPerRecord = totalRecordCount > 0 ? Math.round(weightedCostSum / totalRecordCount) : 165

  // Determine confidence level
  const hasTech = Object.keys(techResponses).length > 0
  const hasData = dataBreachCostByCategory.length > 0
  const hasMaturity = Object.keys(maturityResponses).length > 0
  const dataSources = []
  if (hasMaturity) dataSources.push('maturity')
  if (hasTech) dataSources.push('technology')
  if (hasData) dataSources.push('data-volumes')

  const calibrationConfidence = dataSources.length >= 3 ? 'high' : dataSources.length >= 2 ? 'medium' : 'low'

  return {
    scenarioAdjustments,
    dataBreachCostByCategory,
    totalRecordCount,
    weightedAverageCostPerRecord,
    calibrationConfidence,
    dataSources
  }
}
